import type { ReactNode } from 'react'
import { useMemo } from 'react'

import {
  Space,
  Text,
  StatusBadge,
  WithTooltip,
  Icon,
  type IconTypes,
} from '@procurify/ui'

import { useProcurifyIntl, useFormatBudgetValues } from '@webapp/hooks'
import { SubtotalIndicatorStyled } from './styles/SubtotalIndicatorStyled'
import messages from '../../messages'

export interface BudgetCategoryCardSubtotalProps {
  label: string
  amount: number
  labelTooltip?: ReactNode | string
  labelIcon?: IconTypes
  percentage?: number
  percentageOverBudget?: number
  isTotal?: boolean
  indicatorColor?:
    | 'budgets.committed'
    | 'budgets.pending'
    | 'budgets.available'
    | 'budgets.overBudget'
    | 'transparent'
  indicatorStriped?: boolean
  shouldRenderPercentageFirst?: boolean
  shouldRenderOverageTagBelowText?: boolean
  shouldBoldAmount?: boolean
  shouldHidePercentage?: boolean
  formatSpacing?: {
    rowGap?: 'xsmall' | 'small' | 'medium' | 'large'
  }
}

export const BudgetCategoryCardSubtotal = ({
  label,
  labelTooltip,
  labelIcon,
  amount,
  percentage,
  percentageOverBudget = undefined,
  isTotal,
  indicatorColor,
  indicatorStriped = false,
  shouldRenderPercentageFirst = false,
  shouldRenderOverageTagBelowText = false,
  shouldBoldAmount = false,
  shouldHidePercentage = false,
  formatSpacing = {
    rowGap: 'medium',
  },
}: BudgetCategoryCardSubtotalProps) => {
  const intl = useProcurifyIntl()
  const { formatBudgetPercent, formatBudgetPrice } = useFormatBudgetValues()

  const showIndicator = Boolean(indicatorColor)
  const showLabelTooltip = Boolean(labelTooltip)
  const showLabelIcon = Boolean(labelIcon)
  const showIsOverBudget =
    percentageOverBudget && percentageOverBudget > 0 && isTotal
  const renderOverageTagInline =
    showIsOverBudget && !shouldRenderOverageTagBelowText
  const renderOverageTagBelowNumbers =
    showIsOverBudget && shouldRenderOverageTagBelowText

  const generateStatusLabel = useMemo(
    () => (
      <Space.Item grow={0}>
        {showIsOverBudget && (
          <StatusBadge
            type='error'
            text={intl.formatMessage(messages.subtotalsIsOverBudgetExtraText, {
              overage: formatBudgetPercent(percentageOverBudget.toString()),
            })}
          />
        )}
      </Space.Item>
    ),
    [showIsOverBudget, percentageOverBudget]
  )

  return (
    <Space direction='vertical' size='xsmall'>
      <Space direction='horizontal' size={formatSpacing.rowGap}>
        <Space.Item grow={1} shrink={1}>
          <Space size='small' align='baseline' direction='horizontal'>
            {showIndicator && (
              <Space.Item>
                <SubtotalIndicatorStyled
                  indicatorStriped={indicatorStriped}
                  indicatorColor={indicatorColor}
                />
              </Space.Item>
            )}
            <Space.Item>
              <Text bold={isTotal}>{label}</Text>
            </Space.Item>
            {showLabelTooltip && (
              <Space.Item>
                <WithTooltip title={labelTooltip}>
                  <Icon icon='question' color={'textSecondary'} />
                </WithTooltip>
              </Space.Item>
            )}
            {showLabelIcon && (
              <Space.Item>
                <Icon icon={labelIcon} />
              </Space.Item>
            )}
          </Space>
        </Space.Item>

        {renderOverageTagInline && generateStatusLabel}

        {!shouldHidePercentage && shouldRenderPercentageFirst && (
          <Space.Item minWidth={50}>
            {percentage != null && (
              <Text bold={isTotal} noWrap align='right'>
                {formatBudgetPercent(percentage.toString())}
              </Text>
            )}
          </Space.Item>
        )}

        <Space.Item minWidth={120}>
          <Text bold={isTotal || shouldBoldAmount} noWrap align='right'>
            {formatBudgetPrice(amount.toString())}
          </Text>
        </Space.Item>

        {!shouldHidePercentage && !shouldRenderPercentageFirst && (
          <Space.Item minWidth={50}>
            {percentage != null && (
              <Text bold={isTotal} noWrap align='right'>
                {formatBudgetPercent(percentage.toString())}
              </Text>
            )}
          </Space.Item>
        )}
      </Space>

      {renderOverageTagBelowNumbers && (
        <Space direction='horizontal' justify='end'>
          {generateStatusLabel}
        </Space>
      )}
    </Space>
  )
}
